/**
 * Resolving a sync conflict, one explicit action at a time.
 *
 * WHY THIS IS A SERVICE AND NOT A LINE IN THE SCREEN
 * -------------------------------------------------
 * Because closing a conflict is a WRITE WITH A DECISION INSIDE IT, and three
 * things have to be true together or none of them should be:
 *
 *  1. the local row is still the version the person looked at — or, when the row
 *     has moved on since detection, the version they explicitly re-confirmed;
 *  2. the conflict row is closed against THAT version and that change id;
 *  3. the audit record says who closed it, when, and which change it was about.
 *
 * A screen doing this piecewise can satisfy two of the three. The transaction
 * below makes them one commit: if the audit write fails, the conflict stays open,
 * and the reader never finds a closed conflict with no record of who closed it.
 *
 * WHAT THIS FILE DOES NOT DO
 * --------------------------
 *  - It does not run a sync. `engine.run()` takes the same lock this function
 *    holds, so calling it here would deadlock by design; and resolution must not
 *    advance a cursor or push anything.
 *  - It does not write the entity. `KEEP_LOCAL` keeps what is already there, so
 *    the only writes are the conflict row and the audit row.
 *  - It does not offer `USE_REMOTE`. Re-applying a refused change is a different
 *    operation with its own validation, and it must not arrive as a side effect of
 *    this one.
 */
import type { Transaction } from 'dexie';
import type { DNRDatabase } from '@data/db/database';
import type { AuditLog } from '@data/db/types';
import { planKeepLocal } from '@domain/sync/conflictRecovery';
import type { SyncConflict } from '@domain/sync/conflictModel';
import { ulid } from '@platform/ids';
import { tableNameFor } from './applyChanges';
import { currentDeviceId } from './deviceIdentity';
import { acquireSyncLock } from './syncLock';

export type KeepLocalRefusal =
  /** The conflict row is gone — another window resolved or discarded it. */
  | 'NOT_FOUND'
  /** Already closed, by a different action. Refuse rather than report success. */
  | 'ALREADY_RESOLVED'
  /** The local row moved on since detection. See `planKeepLocal`. */
  | 'STALE_LOCAL'
  /** The stored change no longer matches the conflict it is attached to. */
  | 'CHANGE_MISMATCH'
  /** The applier has no table for this entity type. A bug, not a user situation. */
  | 'UNSUPPORTED_ENTITY';

export type KeepLocalResult =
  | {
      readonly ok: true;
      /** True when a previous identical acknowledgement had already closed it. */
      readonly alreadyResolved: boolean;
      readonly conflict: SyncConflict;
    }
  | {
      readonly ok: false;
      readonly code: KeepLocalRefusal;
      readonly message: string;
      /**
       * The local version to review. Present for `STALE_LOCAL`, so a caller can
       * offer the explicit second confirmation instead of leaving the record
       * un-actionable. Never present for any other refusal.
       */
      readonly currentLocalVersion?: number;
    };

export interface KeepLocalOptions {
  /** Injected clock, so a test can assert the recorded time. */
  readonly now?: () => string;
  /**
   * The signed-in account, when there is one.
   *
   * Absent stays absent: an acknowledgement made with nobody signed in records
   * the device and the app's long-standing "local" actor, never a fabricated
   * person. `AuthUser.name` does not exist, so the caller passes what it has.
   */
  readonly actor?: { readonly userId: string; readonly userName?: string } | null;
  /**
   * The local version the caller has DISPLAYED and the person confirmed.
   *
   * Only for the second attempt after a `STALE_LOCAL` refusal. It is checked
   * against the row inside the transaction, so a stale value refuses exactly like
   * no value at all — this names what was shown, it does not skip the check.
   */
  readonly reviewedLocalVersion?: number;
  /**
   * The audit write. Injectable ONLY so a test can prove the rollback: with a
   * failing writer, the conflict must stay open and no audit row may exist.
   */
  readonly writeAudit?: (tx: Transaction, entry: AuditLog) => Promise<unknown>;
}

function defaultWriteAudit(tx: Transaction, entry: AuditLog): Promise<unknown> {
  return tx.table('auditLogs').add(entry) as Promise<unknown>;
}

function refusal(
  code: KeepLocalRefusal,
  message: string,
  currentLocalVersion?: number,
): KeepLocalResult {
  return { ok: false, code, message, ...(currentLocalVersion === undefined ? {} : { currentLocalVersion }) };
}

/** `syncVersion` of a row as read back from Dexie; 0 when the row is gone. */
function versionOf(row: unknown): number {
  const value = (row as { syncVersion?: unknown } | undefined)?.syncVersion;
  return typeof value === 'number' ? value : 0;
}

/**
 * Keep the version this device already holds, and close the conflict.
 *
 * IDEMPOTENT BY CONSTRUCTION, not by retry bookkeeping:
 *
 *  - the conflict row is addressed by its own id, so a repeated click resolves
 *    the same row and can never touch a different one;
 *  - a row that is already closed with `resolution: 'LOCAL'` is reported as
 *    success WITHOUT writing, so a double-click does not append a second audit
 *    row or move `resolvedAt`;
 *  - a row closed by any other action is refused, so this can never silently
 *    overwrite somebody else's decision.
 */
export async function keepLocalVersion(
  db: DNRDatabase,
  conflictId: string,
  options: KeepLocalOptions = {},
): Promise<KeepLocalResult> {
  // The lock key comes from the row, so it is read before the lock is taken. The
  // row is re-read inside the transaction; this read only decides what to lock.
  const probe = (await db.syncConflicts.get(conflictId)) as SyncConflict | undefined;
  if (!probe) {
    return refusal('NOT_FOUND', 'This conflict is no longer on this device.');
  }

  const tableName = tableNameFor(probe.entityType);
  if (!tableName) {
    return refusal(
      'UNSUPPORTED_ENTITY',
      `This build does not know how to check a "${probe.entityType}" record, so the conflict cannot be acknowledged here.`,
    );
  }

  // Same lock the engine takes: a pull must not write the row between the version
  // check and the close. The lock is in-memory and per business, never re-entered
  // by this function, and `engine.run()` is deliberately NOT called under it.
  const release = await acquireSyncLock(probe.businessId);
  try {
    const deviceId = await currentDeviceId(db);
    const now = options.now ?? (() => new Date().toISOString());
    const table = db.table(tableName);

    return await db.transaction('rw', table, db.syncConflicts, db.auditLogs, async (tx) => {
      const conflict = (await tx.table('syncConflicts').get(conflictId)) as
        | SyncConflict
        | undefined;
      if (!conflict) {
        return refusal('NOT_FOUND', 'This conflict is no longer on this device.');
      }
      if (conflict.resolved) {
        if (conflict.resolution === 'LOCAL') {
          return { ok: true, alreadyResolved: true, conflict } as const;
        }
        return refusal(
          'ALREADY_RESOLVED',
          'This conflict was already settled another way, so it was left as it is.',
        );
      }

      // The version the person is looking at, read NOW rather than trusted from
      // the detection time. A row edited in between makes "keep the version I
      // have" a different promise than the one they were making.
      const currentLocalVersion = versionOf(await tx.table(tableName).get(conflict.entityId));
      const check = planKeepLocal({
        conflict,
        currentLocalVersion,
        reviewedLocalVersion: options.reviewedLocalVersion,
      });
      if (!check.ok) return refusal(check.code, check.message, check.currentLocalVersion);

      const resolvedAt = now();
      const actor = options.actor ?? null;
      const resolved: SyncConflict = {
        ...conflict,
        resolved: true,
        resolution: 'LOCAL',
        resolvedAt,
        resolvedByDeviceId: deviceId,
        // Which version was actually kept — not necessarily the detection one.
        // `localVersion` above is left exactly as recorded: it is the history of
        // why this conflict exists, not a value the acknowledgement may rewrite.
        resolvedLocalVersion: currentLocalVersion,
        ...(actor ? { resolvedByUserId: actor.userId } : {}),
      };
      await tx.table('syncConflicts').put(resolved);

      const entry: AuditLog = {
        id: ulid(),
        businessId: conflict.businessId,
        entityType: 'SYNC',
        // The business record, so "everything that happened to this invoice" still
        // finds it through the entityId index; `entityType: 'SYNC'` says what kind
        // of event it was.
        entityId: conflict.entityId,
        action: 'RESOLVE',
        before: JSON.stringify({
          conflictId: conflict.conflictId,
          resolved: false,
          reason: conflict.reason,
          localVersion: conflict.localVersion,
          remoteVersion: conflict.remoteVersion,
        }),
        after: JSON.stringify({
          conflictId: conflict.conflictId,
          changeId: conflict.pendingChange?.changeId ?? null,
          entityType: conflict.entityType,
          entityId: conflict.entityId,
          resolution: 'KEEP_LOCAL',
          localVersion: conflict.localVersion,
          remoteVersion: conflict.remoteVersion,
          keptVersion: currentLocalVersion,
          // DETECTION_VERSION: the row was still the version the conflict named.
          // REVIEWED_VERSION: it had moved on and the person confirmed the newer
          // one; the audit is the only place the distinction is recorded.
          acknowledgedBasis: check.basis,
          pendingChangeOmitted: conflict.pendingChangeOmitted ?? null,
          deviceId,
          actorSource: actor ? 'signed-in' : 'local-device',
        }),
        userId: actor?.userId ?? 'local',
        userName: actor?.userName ?? 'You',
        deviceId,
        occurredAt: resolvedAt,
        reason: 'Sync conflict acknowledged: this device kept its version.',
      };
      // LAST, and inside the same transaction. If this throws, Dexie rolls back
      // the conflict write above with it — a closed conflict without a record of
      // who closed it is exactly the kind of hole an audit trail exists to prevent.
      await (options.writeAudit ?? defaultWriteAudit)(tx, entry);

      return { ok: true, alreadyResolved: false, conflict: resolved } as const;
    });
  } finally {
    release();
  }
}
